// src/components/Control/Operator/VoiceOverlay.tsx
// Голосовой слой поверх любого экрана: «Слушаю…» → распознано → выполняю →
// результат или ошибка. Нажать микрофон ещё раз — отмена (ничего не уходит).
// STOP доступен прямо в слое. Неуверенно распознанное движение — только после подтверждения.
import React, { useEffect, useState } from 'react';
import { useRobot, useUi, useUiActions, useVoiceState } from '../robot/RobotContext';
import { VOICE_EXAMPLES } from '../robot/voice';
import { StopButton } from './Header';
import { Icon } from './ui';

const PHASE_TEXT: Record<string, string> = {
  idle: 'Готов слушать',
  listening: 'Слушаю…',
  recognized: 'Распознано',
  confirm: 'Подтвердите команду',
  executing: 'Выполняю…',
  success: 'Готово',
  error: 'Не получилось',
  cancelled: 'Отменено',
};

const VoiceOverlay: React.FC = () => {
  const { voice, session } = useRobot();
  const open = useUi(u => u.voiceOpen);
  const screen = useUi(u => u.screen);
  const { toggleVoice } = useUiActions();
  const v = useVoiceState(s => s);
  const [text, setText] = useState('');

  // Итог показывается пару секунд, потом слой закрывается сам
  useEffect(() => {
    if (!open) return;
    if (v.phase === 'success' || v.phase === 'cancelled') {
      const t = setTimeout(() => {
        toggleVoice(false);
        voice.reset();
      }, v.phase === 'success' ? 2500 : 1200);
      return () => clearTimeout(t);
    }
  }, [open, v.phase, voice, toggleVoice]);

  if (!open) return null;

  const close = () => {
    voice.reset();
    toggleVoice(false);
  };
  const listening = v.phase === 'listening';
  const bars = [0.6, 1, 0.8, 0.5].map(k => Math.max(0.15, Math.min(1, v.level * 6 * k)));
  const hint = screen === 'camera' ? '«включи камеру 2», «что видишь»'
    : screen === 'nanny' ? '«включи режим няни», «скажи привет»'
      : screen === 'settings' ? '«включи режим няни», «громче»'
        : '«вперёд», «следуй за мной», «что на карте»';

  return (
    <div className="op-voice-backdrop" onClick={e => e.target === e.currentTarget && close()}>
      <div className={`op-voice ${v.phase}`} role="dialog" aria-modal="true" aria-label="Голосовая команда">
        <button type="button" className="op-icon-btn op-voice-close" aria-label="Закрыть" onClick={close}><Icon name="close" /></button>
        <div className="op-voice-wave" aria-hidden="true">
          {bars.map((h, i) => <i key={i} style={{ transform: `scaleY(${listening ? h : 0.2})` }} />)}
        </div>
        <div className="op-voice-phase" role="status" aria-live="assertive">{PHASE_TEXT[v.phase]}</div>
        {v.heard && <div className="op-voice-heard">«{v.heard}»</div>}
        {v.message && <div className={`op-voice-msg ${v.phase}`}>{v.message}</div>}

        <div className="op-voice-actions">
          {listening && (
            <>
              <button type="button" className="op-btn primary big" onClick={() => voice.finish()}>Готово — отправить</button>
              <button type="button" className="op-btn big" onClick={() => voice.cancel()}>Отмена</button>
            </>
          )}
          {v.phase === 'confirm' && (
            <>
              <button type="button" className="op-btn primary big" onClick={() => voice.confirm()}>Да, выполнить</button>
              <button type="button" className="op-btn big" onClick={() => voice.cancel()}>Нет</button>
            </>
          )}
          {(v.phase === 'error' || v.phase === 'success' || v.phase === 'idle' || v.phase === 'cancelled') && (
            <button type="button" className="op-btn primary big" disabled={!session} onClick={() => voice.startCommand()}>
              <Icon name="mic" size={18} /> {v.phase === 'error' ? 'Сказать ещё раз' : 'Сказать команду'}
            </button>
          )}
        </div>
        <div className="op-voice-stop"><StopButton compact={v.phase !== 'executing' && v.phase !== 'recognized'} /></div>

        {!listening && (
          <form className="op-row op-voice-text" onSubmit={e => { e.preventDefault(); voice.sendText(text); setText(''); }}>
            <input type="text" value={text} onChange={e => setText(e.target.value)} placeholder="или напишите команду"
                   aria-label="Текстовая команда" />
            <button type="submit" className="op-btn" disabled={!session || !text.trim()}>Выполнить</button>
          </form>
        )}
        <p className="op-muted op-voice-hint">
          Например: {hint}. Распознавание: {v.recognizer === 'robot' ? 'на роботе' : 'в браузере'}.
        </p>
        <details className="op-voice-examples">
          <summary>Все команды</summary>
          <div className="op-chips">
            {VOICE_EXAMPLES.map(e => (
              <button key={e} type="button" className="op-chip-btn" onClick={() => voice.sendText(e)} disabled={!session}>{e}</button>
            ))}
          </div>
        </details>
      </div>
    </div>
  );
};

export default VoiceOverlay;
